import { FC } from 'react';

import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';

import { ChangeIndicator } from '@/components/atoms';
import { Card, CardContent } from '@/components/atoms/Card/Card';
import { GaugeChartColors } from '@/components/molecules/GaugeChart/constants';
import { GaugeChartSkeleton } from '@/components/molecules/GaugeChart/GaugeChartSkeleton';
import { ENPS_GAUGE_CHART_TEST_IDS } from '@/constants/data-testids';
import { formatEnpsScoreForDisplay } from '@/utils/enpsDisplay';
import { cn } from '@/utils/tailwind';

type EnpsGaugeChartProps = {
  score: number;
  previousScore: number;
  companyScore: number;
  minScore?: number;
  maxScore?: number;
  isLoading?: boolean;
  className?: string;
};

const getColor = (score: number) => {
  if (score < 0) return GaugeChartColors.RED;
  if (score < 50) return GaugeChartColors.ORANGE;
  return GaugeChartColors.GREEN;
};

const getAngle = (value: number, minScore: number, maxScore: number) => {
  const clampedValue = Math.min(Math.max(value, minScore), maxScore);
  return ((clampedValue - minScore) / (maxScore - minScore)) * 180;
};

const getCompanyBadgeClassName = (companyScore: number) => {
  if (companyScore < 0) return 'bg-custom-red-100 !text-custom-red-700';
  if (companyScore > 0) return 'bg-custom-emerald-100 !text-custom-emerald-700';
  return 'bg-custom-warm-grey-100 !text-custom-warm-grey-700';
};

export const EnpsGaugeChart: FC<EnpsGaugeChartProps> = ({
  score,
  previousScore,
  companyScore,
  minScore = -100,
  maxScore = 100,
  isLoading,
  className,
}) => {
  const scoreDifference = score - previousScore;
  const scoreAngle = getAngle(score, minScore, maxScore);
  const previousScoreAngle = getAngle(previousScore, minScore, maxScore);

  if (isLoading) {
    return <GaugeChartSkeleton description="" />;
  }

  return (
    <Card
      className={cn(
        'flex min-h-[310px] items-center justify-center rounded-[12px] bg-custom-warm-grey-25 px-6 py-8',
        className
      )}
    >
      <CardContent className="w-full max-w-[330px]">
        <div className="relative">
          <ResponsiveContainer className="mb-9" width="100%" height={155}>
            <PieChart margin={{ top: 155 }}>
              <Pie
                data={[{ value: 2 }, { value: 1 }, { value: 1 }]}
                startAngle={180}
                endAngle={0}
                innerRadius={122}
                outerRadius={148}
                dataKey="value"
                stroke="none"
                paddingAngle={5}
                cornerRadius={10}
                isAnimationActive={false}
              >
                <Cell fill={GaugeChartColors.RED} />
                <Cell fill={GaugeChartColors.ORANGE} />
                <Cell fill={GaugeChartColors.GREEN} />
              </Pie>
            </PieChart>
          </ResponsiveContainer>

          <GaugeMarker angle={previousScoreAngle} variant="previous" />
          <GaugeMarker angle={scoreAngle} color={getColor(score)} variant="current" />

          <div className="absolute left-1/2 top-[86px] -translate-x-1/2 text-center">
            <div className="flex items-center justify-center gap-2">
              <p className="text-body-36-medium !text-custom-warm-grey-950">
                {formatEnpsScoreForDisplay(score)}
              </p>
              <ChangeIndicator changeValue={scoreDifference} valueUnit="points" />
            </div>
            <p className="text-body-14-medium !text-custom-warm-grey-500">
              {formatEnpsScoreForDisplay(previousScore)} previously
            </p>
          </div>

          <p className="absolute -bottom-[20px] left-[5px] text-body-12-regular !text-custom-warm-grey-500">
            {minScore}
          </p>
          <p className="absolute -bottom-[20px] right-[5px] text-body-12-regular !text-custom-warm-grey-500">
            {maxScore}
          </p>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2">
          <span className="text-body-16-medium !text-custom-warm-grey-500">Company average:</span>
          <span
            className={cn(
              'rounded-[6px] px-2 py-[3px] text-body-12-medium',
              getCompanyBadgeClassName(companyScore)
            )}
          >
            {formatEnpsScoreForDisplay(companyScore)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

type GaugeMarkerProps = {
  angle: number;
  color?: string;
  variant: 'current' | 'previous';
};

const GaugeMarker: FC<GaugeMarkerProps> = ({ angle, color, variant }) => {
  const isCurrent = variant === 'current';

  return (
    <div
      data-testid={
        isCurrent
          ? ENPS_GAUGE_CHART_TEST_IDS.CURRENT_MARKER
          : ENPS_GAUGE_CHART_TEST_IDS.PREVIOUS_MARKER
      }
      className={cn(
        'absolute bottom-0 left-1/2 flex origin-bottom rounded-full transition-opacity'
      )}
      style={{
        transform: `translateX(-50%) rotate(${angle - 90}deg)`,
        transformOrigin: 'bottom center',
        height: '158px',
      }}
    >
      <span
        className={cn(
          'h-11 w-4 rounded-full',
          isCurrent ? 'bg-white' : 'bg-custom-red-100 opacity-90'
        )}
        style={isCurrent ? { border: `2px solid ${color}` } : { border: `2px solid #fff` }}
      />
    </div>
  );
};
